import { ENTITY_TYPES, EntityType } from './entity-type';

export const PRESENTATION_ACCESS = {
  EDITOR: 'presentation:editor',
  PUBLISHER: 'presentation:publisher',
  FULL_ACCESS: 'presentation:fullAccess',
} as const;

export type PresentationAccess =
  (typeof PRESENTATION_ACCESS)[keyof typeof PRESENTATION_ACCESS];

export const SCHEDULE_ACCESS = {
  PLAYLIST_MANAGER: 'schedule:playlistManager',
  FULL_ACCESS: 'schedule:fullAccess',
} as const;

export type ScheduleAccess =
  (typeof SCHEDULE_ACCESS)[keyof typeof SCHEDULE_ACCESS];

export const DISPLAY_ACCESS = {
  SCREEN_SHARING_MODERATOR: 'display:screenSharingModerator',
  FULL_ACCESS: 'display:fullAccess',
} as const;

export type DisplayAccess =
  (typeof DISPLAY_ACCESS)[keyof typeof DISPLAY_ACCESS];

export type AccessLevel = PresentationAccess | ScheduleAccess | DisplayAccess;

export interface AccessRequirement {
  requireAccess?: AccessLevel;
  requireRole?: string;
  allowAssignedPublisher?: boolean;
  allowSharedAccess?: boolean;
  entityId?: string;
}

export type ShareAccessLabel =
  | 'Editor'
  | 'Publisher'
  | 'Playlist Manager'
  | 'Screen Sharing Moderator'
  | 'Full Access';

const SHARE_LABEL_ACCESS: Record<
  Exclude<ShareAccessLabel, 'Full Access'>,
  AccessLevel
> = {
  Editor: PRESENTATION_ACCESS.EDITOR,
  Publisher: PRESENTATION_ACCESS.PUBLISHER,
  'Playlist Manager': SCHEDULE_ACCESS.PLAYLIST_MANAGER,
  'Screen Sharing Moderator': DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR,
};

const FULL_ACCESS_BY_ENTITY: Record<EntityType, AccessLevel> = {
  [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.FULL_ACCESS,
  [ENTITY_TYPES.SCHEDULE]: SCHEDULE_ACCESS.FULL_ACCESS,
  [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.FULL_ACCESS,
};

export function shareLabelToAccessLevel(
  label: string,
  entityType: EntityType,
): AccessLevel | null {
  if (label === 'Full Access') {
    return FULL_ACCESS_BY_ENTITY[entityType] ?? null;
  }

  const mapped =
    SHARE_LABEL_ACCESS[label as Exclude<ShareAccessLabel, 'Full Access'>];

  if (!mapped || getEntityType(mapped) !== entityType) {
    return null;
  }

  return mapped;
}

const ACCESS_RANK: Record<AccessLevel, number> = {
  [PRESENTATION_ACCESS.EDITOR]: 1,
  [PRESENTATION_ACCESS.PUBLISHER]: 2,
  [PRESENTATION_ACCESS.FULL_ACCESS]: 3,
  [SCHEDULE_ACCESS.PLAYLIST_MANAGER]: 1,
  [SCHEDULE_ACCESS.FULL_ACCESS]: 2,
  [DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR]: 1,
  [DISPLAY_ACCESS.FULL_ACCESS]: 2,
};

export const FOLDER_ACCESS_BY_ENTITY: Record<EntityType, AccessLevel> = {
  [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.EDITOR,
  [ENTITY_TYPES.SCHEDULE]: SCHEDULE_ACCESS.PLAYLIST_MANAGER,
  [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.FULL_ACCESS,
};

export function getFolderAccessLevel(
  kind: EntityType | 'Presentation' | 'Schedule' | 'Display',
): AccessLevel {
  return FOLDER_ACCESS_BY_ENTITY[kind.toLowerCase() as EntityType];
}

export function getEntityType(level: AccessLevel): EntityType | null {
  const prefix = level.split(':')[0];

  return (
    (Object.values(ENTITY_TYPES) as readonly EntityType[]).find(
      (entityType) => entityType === prefix,
    ) ?? null
  );
}

export function getAccessLevelRank(level: AccessLevel): number | null {
  return ACCESS_RANK[level] ?? null;
}

export function meetsAccessLevel(
  actual: AccessLevel,
  required: AccessLevel,
): boolean {
  if (getEntityType(actual) !== getEntityType(required)) {
    return false;
  }

  const actualRank = getAccessLevelRank(actual);
  const requiredRank = getAccessLevelRank(required);

  if (actualRank === null || requiredRank === null) {
    return false;
  }

  return actualRank >= requiredRank;
}
